import axios from 'axios';
import { client } from '@/services/api/axios.ts';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

export type RangeGranularity = '5min' | 'hourly' | 'daily' | 'monthly';

export type TDashboardEnergyManagementRecord = {
  Battery_Charge: number;
  Battery_Discharge: number;
  Battery_State_of_Charge: number;
  Consumed: number;
  Consumed_from_Grid: number;
  Fed_to_Grid: number;
  Yield: number;
  time: string;
};

export type TDashboardOverallPerformance = {
  Battery_Charge: number;
  Battery_Discharge: number;
  Consumed: number;
  Consumed_from_Grid: number;
  Fed_to_Grid: number;
  Revenue: number;
  Yield: number;
  revenueChart: { value: number; field: string; time: string }[];
};

export type TDashboardResponse = {
  energyManagement: TDashboardEnergyManagementRecord[];
  overallPerformance: TDashboardOverallPerformance;
  rangeGranularity: RangeGranularity;
};

export async function getOverviewPlant({
  dateRange,
  viewContext,
}: {
  dateRange: {
    startDate: Date;
    endDate: Date | undefined;
    rangeGranularity: RangeGranularity;
  };
  viewContext: string;
}): Promise<TDashboardResponse | undefined | never> {
  try {
    const searchParams = new URLSearchParams();
    searchParams.set('viewContext', viewContext);
    searchParams.set('granularity', dateRange.rangeGranularity);
    searchParams.set('startDate', `${new Date(dateRange.startDate).getTime()}`);
    searchParams.set(
      'endDate',
      dateRange.endDate ? `${new Date(dateRange.endDate).getTime()}` : ''
    );

    const { data } = await client.get(`/dashboard/overview?${searchParams.toString()}`);

    data.body.rangeGranularity = dateRange.rangeGranularity;
    return data?.body;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(`${error?.response?.data?.message}`);
    }

    if (error instanceof Error) {
      throw new Error(`${error.message}}`);
    }
  }
}

export const useOverviewQuery = ({
  dateRange,
  viewContext,
}: {
  dateRange: {
    startDate: Date;
    endDate: Date | undefined;
    rangeGranularity: RangeGranularity;
  };
  viewContext: string;
}) => {
  return useQuery({
    queryKey: [
      'overview',
      viewContext,
      dateRange.rangeGranularity,
      dateRange.startDate,
      dateRange.endDate,
    ],
    queryFn: () => getOverviewPlant({ dateRange, viewContext }),
    retry: 1,
    placeholderData: keepPreviousData,
    refetchInterval: 1000 * 60,
  });
};
